import { OperationOutcomeError, normalizeOperationOutcome } from '@medplum/core';
import type { PatchOperation, WithId } from '@medplum/core';
import type { Binary, Bundle, ExtractResource, Resource, ResourceType } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import { medplumCacheUtilities as medplumCacheUtilities } from './cache-utils';

/**
 * Options for mutation hooks that allow customizing cache invalidation behavior.
 */
export type MutationOptions<TData, TVariables, TError = OperationOutcomeError> = {
  /**
   * Whether to automatically invalidate related queries after mutation.
   * @default true
   */
  autoInvalidate?: boolean;
  /**
   * Additional mutation options to pass to TanStack Query.
   */
  mutationOptions?: Omit<
    UseMutationOptions<TData, TError, TVariables>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (data: TData, variables: TVariables, context: unknown) => void | Promise<void>;
  };
};

/**
 * React Hook for creating a FHIR resource using TanStack Query mutations.
 *
 * Automatically invalidates related queries after successful creation.
 *
 * @param options - Optional configuration for the mutation.
 * @returns TanStack Query mutation result.
 *
 * @example
 * ```tsx
 * const createPatient = useCreateResource<Patient>();
 *
 * const handleCreate = async () => {
 *   const newPatient = await createPatient.mutateAsync({
 *     resourceType: 'Patient',
 *     name: [{ given: ['John'], family: 'Doe' }]
 *   });
 *   console.log('Created:', newPatient.id);
 * };
 * ```
 *
 * @example With conditional create (createResourceIfNoneExist)
 * ```tsx
 * const createPatient = useCreateResource<Patient>();
 *
 * await createPatient.mutateAsync({
 *   resource: {
 *     resourceType: 'Patient',
 *     identifier: [{ system: 'http://example.com/mrn', value: '123' }]
 *   },
 *   ifNoneExist: 'identifier=http://example.com/mrn|123'
 * });
 * ```
 */
export function useCreateResource<T extends Resource>(
  options?: MutationOptions<
    WithId<T>,
    | T
    | {
      resource: T;
      ifNoneExist: string;
    }
  >,
): UseMutationResult<
  WithId<T>,
  OperationOutcomeError,
  | T
  | {
    resource: T;
    ifNoneExist: string;
  }
> {
  const medplum = useMedplum();
  const queryClient = useQueryClient();
  const autoInvalidate = options?.autoInvalidate ?? true;

  const { onSuccess: userOnSuccess, ...restMutationOptions } = options?.mutationOptions || {};

  return useMutation({
    mutationFn: async (input) => {
      try {
        // Check if this is a conditional create
        if (typeof input === 'object' && 'ifNoneExist' in input && 'resource' in input) {
          return await medplum.createResourceIfNoneExist(input.resource, input.ifNoneExist);
        }
        // Regular create
        return await medplum.createResource(input as T);
      } catch (error) {
        throw new OperationOutcomeError(normalizeOperationOutcome(error), { cause: error });
      }
    },
    onSuccess: async (data, variables, context) => {
      if (autoInvalidate) {
        const resourceType = data.resourceType;
        await medplumCacheUtilities.invalidateResource(queryClient, resourceType);
      }
      // Call user's onSuccess if provided
      if (userOnSuccess) {
        await userOnSuccess(data, variables, context);
      }
    },
    ...restMutationOptions,
  });
}

/**
 * React Hook for updating a FHIR resource using TanStack Query mutations.
 *
 * Automatically invalidates related queries after successful update.
 *
 * @param options - Optional configuration for the mutation.
 * @returns TanStack Query mutation result.
 *
 * @example
 * ```tsx
 * const updatePatient = useUpdateResource<Patient>();
 *
 * const handleUpdate = async () => {
 *   const updated = await updatePatient.mutateAsync({
 *     resourceType: 'Patient',
 *     id: '123',
 *     name: [{ given: ['Jane'], family: 'Doe' }]
 *   });
 *   console.log('Updated:', updated.meta?.versionId);
 * };
 * ```
 */
export function useUpdateResource<T extends Resource>(
  options?: MutationOptions<WithId<T>, T>,
): UseMutationResult<WithId<T>, OperationOutcomeError, T> {
  const medplum = useMedplum();
  const queryClient = useQueryClient();
  const autoInvalidate = options?.autoInvalidate ?? true;

  const { onSuccess: userOnSuccess, ...restMutationOptions } = options?.mutationOptions || {};

  return useMutation({
    mutationFn: async (resource) => {
      try {
        return await medplum.updateResource(resource);
      } catch (error) {
        throw new OperationOutcomeError(normalizeOperationOutcome(error), { cause: error });
      }
    },
    onSuccess: async (data, variables, context) => {
      if (autoInvalidate) {
        const resourceType = data.resourceType;
        await medplumCacheUtilities.invalidateResource(queryClient, resourceType);
      }
      // Call user's onSuccess if provided
      if (userOnSuccess) {
        await userOnSuccess(data, variables, context);
      }
    },
    ...restMutationOptions,
  });
}

/**
 * React Hook for upserting a FHIR resource using TanStack Query mutations.
 *
 * Upserts the resource: updates it if it exists, creates it if it doesn't.
 * This is done in a single, transactional request to guarantee data consistency.
 *
 * Automatically invalidates related queries after successful upsert.
 *
 * @param options - Optional configuration for the mutation.
 * @returns TanStack Query mutation result.
 *
 * @example
 * ```tsx
 * const upsertPatient = useUpsertResource<Patient>();
 *
 * const handleUpsert = async () => {
 *   const patient = await upsertPatient.mutateAsync({
 *     resource: {
 *       resourceType: 'Patient',
 *       identifier: [{ system: 'http://example.com/mrn', value: '123' }],
 *       name: [{ given: ['John'], family: 'Doe' }]
 *     },
 *     query: { identifier: 'http://example.com/mrn|123' }
 *   });
 *   console.log('Upserted:', patient.id);
 * };
 * ```
 */
export function useUpsertResource<T extends Resource>(
  options?: MutationOptions<
    WithId<T>,
    {
      resource: T;
      query: Record<string, string | number | boolean | undefined>;
    }
  >,
): UseMutationResult<
  WithId<T>,
  OperationOutcomeError,
  {
    resource: T;
    query: Record<string, string | number | boolean | undefined>;
  }
> {
  const medplum = useMedplum();
  const queryClient = useQueryClient();
  const autoInvalidate = options?.autoInvalidate ?? true;

  const { onSuccess: userOnSuccess, ...restMutationOptions } = options?.mutationOptions || {};

  return useMutation({
    mutationFn: async ({ resource, query }) => {
      try {
        return await medplum.upsertResource(resource, query);
      } catch (error) {
        throw new OperationOutcomeError(normalizeOperationOutcome(error), { cause: error });
      }
    },
    onSuccess: async (data, variables, context) => {
      if (autoInvalidate) {
        const resourceType = data.resourceType;
        await medplumCacheUtilities.invalidateResource(queryClient, resourceType);
      }
      // Call user's onSuccess if provided
      if (userOnSuccess) {
        await userOnSuccess(data, variables, context);
      }
    },
    ...restMutationOptions,
  });
}

/**
 * React Hook for patching a FHIR resource using JSONPatch operations with TanStack Query mutations.
 *
 * Automatically invalidates related queries after successful patch.
 *
 * @param options - Optional configuration for the mutation.
 * @returns TanStack Query mutation result.
 *
 * @example
 * ```tsx
 * const patchPatient = usePatchResource<'Patient'>();
 *
 * const handlePatch = async () => {
 *   const patched = await patchPatient.mutateAsync({
 *     resourceType: 'Patient',
 *     id: '123',
 *     operations: [
 *       { op: 'replace', path: '/name/0/family', value: 'Smith' },
 *       { op: 'add', path: '/telecom/-', value: { system: 'phone', value: '555-1234' } }
 *     ]
 *   });
 *   console.log('Patched:', patched.meta?.versionId);
 * };
 * ```
 */
export function usePatchResource<RT extends ResourceType>(
  options?: MutationOptions<
    WithId<ExtractResource<RT>>,
    {
      resourceType: RT;
      id: string;
      operations: PatchOperation[];
    }
  >,
): UseMutationResult<
  WithId<ExtractResource<RT>>,
  OperationOutcomeError,
  {
    resourceType: RT;
    id: string;
    operations: PatchOperation[];
  }
> {
  const medplum = useMedplum();
  const queryClient = useQueryClient();
  const autoInvalidate = options?.autoInvalidate ?? true;

  const { onSuccess: userOnSuccess, ...restMutationOptions } = options?.mutationOptions || {};

  return useMutation({
    mutationFn: async ({ resourceType, id, operations }) => {
      try {
        return await medplum.patchResource(resourceType, id, operations);
      } catch (error) {
        throw new OperationOutcomeError(normalizeOperationOutcome(error), { cause: error });
      }
    },
    onSuccess: async (data, variables, context) => {
      if (autoInvalidate) {
        const resourceType = variables.resourceType;
        await medplumCacheUtilities.invalidateResource(queryClient, resourceType);
      }
      // Call user's onSuccess if provided
      if (userOnSuccess) {
        await userOnSuccess(data, variables, context);
      }
    },
    ...restMutationOptions,
  });
}

/**
 * React Hook for deleting a FHIR resource using TanStack Query mutations.
 *
 * Automatically invalidates related queries after successful deletion.
 *
 * @param options - Optional configuration for the mutation.
 * @returns TanStack Query mutation result.
 *
 * @example
 * ```tsx
 * const deletePatient = useDeleteResource();
 *
 * const handleDelete = async () => {
 *   await deletePatient.mutateAsync({
 *     resourceType: 'Patient',
 *     id: '123'
 *   });
 *   console.log('Deleted successfully');
 * };
 * ```
 */
export function useDeleteResource(
  options?: MutationOptions<
    void,
    {
      resourceType: ResourceType;
      id: string;
    }
  >,
): UseMutationResult<
  void,
  OperationOutcomeError,
  {
    resourceType: ResourceType;
    id: string;
  }
> {
  const medplum = useMedplum();
  const queryClient = useQueryClient();
  const autoInvalidate = options?.autoInvalidate ?? true;

  const { onSuccess: userOnSuccess, ...restMutationOptions } = options?.mutationOptions || {};

  return useMutation({
    mutationFn: async ({ resourceType, id }) => {
      try {
        await medplum.deleteResource(resourceType, id);
      } catch (error) {
        throw new OperationOutcomeError(normalizeOperationOutcome(error), { cause: error });
      }
    },
    onSuccess: async (data, variables, context) => {
      if (autoInvalidate) {
        const resourceType = variables.resourceType;
        await medplumCacheUtilities.invalidateResource(queryClient, resourceType);
      }
      // Call user's onSuccess if provided
      if (userOnSuccess) {
        await userOnSuccess(data, variables, context);
      }
    },
    ...restMutationOptions,
  });
}

/**
 * React Hook for executing a FHIR batch/transaction using TanStack Query mutations.
 *
 * Automatically invalidates caches for all resource types involved in the batch.
 *
 * @param options - Optional configuration for the mutation.
 * @returns TanStack Query mutation result.
 *
 * @example
 * ```tsx
 * const executeBatch = useExecuteBatch();
 *
 * const handleBatch = async () => {
 *   const batch: Bundle = {
 *     resourceType: 'Bundle',
 *     type: 'transaction',
 *     entry: [
 *       {
 *         request: { method: 'POST', url: 'Patient' },
 *         resource: {
 *           resourceType: 'Patient',
 *           name: [{ given: ['Alice'], family: 'Smith' }]
 *         }
 *       },
 *       {
 *         request: { method: 'PUT', url: 'Observation/123' },
 *         resource: {
 *           resourceType: 'Observation',
 *           id: '123',
 *           status: 'final'
 *         }
 *       }
 *     ]
 *   };
 *
 *   const result = await executeBatch.mutateAsync(batch);
 *   console.log('Batch executed:', result.entry?.length, 'operations');
 * };
 * ```
 */
export function useExecuteBatch(
  options?: MutationOptions<Bundle, Bundle>,
): UseMutationResult<Bundle, OperationOutcomeError, Bundle> {
  const medplum = useMedplum();
  const queryClient = useQueryClient();
  const autoInvalidate = options?.autoInvalidate ?? true;

  const { onSuccess: userOnSuccess, ...restMutationOptions } = options?.mutationOptions || {};

  return useMutation({
    mutationFn: async (bundle: Bundle) => {
      try {
        return await medplum.executeBatch(bundle);
      } catch (error) {
        throw new OperationOutcomeError(normalizeOperationOutcome(error), { cause: error });
      }
    },
    onSuccess: async (data, variables, context) => {
      if (autoInvalidate) {
        // Extract all unique resource types from the batch
        const resourceTypes = new Set<ResourceType>();

        // Check input bundle entries
        if (variables.entry) for (const entry of variables.entry) {
          if (entry.resource?.resourceType) {
            resourceTypes.add(entry.resource.resourceType);
          }
          // For PATCH operations, extract resource type from URL (e.g., "Task/123" -> "Task")
          if (entry.request?.url) {
            const urlResourceType = entry.request.url.split('/')[0] as ResourceType;
            if (urlResourceType) {
              resourceTypes.add(urlResourceType);
            }
          }
        }

        // Also check response bundle entries (might have additional resources)
        if (data.entry) for (const entry of data.entry) {
          if (entry.resource?.resourceType) {
            resourceTypes.add(entry.resource.resourceType);
          }
        }

        // Invalidate cache for each affected resource type (both individual resources and searches)
        await Promise.all(
          [...resourceTypes].flatMap(resourceType => [
            medplumCacheUtilities.invalidateResource(queryClient, resourceType),
            medplumCacheUtilities.invalidateSearches(queryClient, resourceType),
          ]),
        );
      }

      // Call user's onSuccess if provided
      if (userOnSuccess) {
        await userOnSuccess(data, variables, context);
      }
    },
    ...restMutationOptions,
  });
}

/**
 * Input type for creating a Binary resource (file upload).
 */
export type CreateBinaryInput = {
  /** The data to upload (File, Blob, string, or ArrayBuffer) */
  data: File | Blob | string | ArrayBuffer;
  /** The MIME type of the content */
  contentType: string;
  /** Optional filename for the binary */
  filename?: string;
};

/**
 * React Hook for creating a Binary resource (file upload) using TanStack Query mutations.
 *
 * Automatically invalidates related queries after successful upload.
 *
 * @param options - Optional configuration for the mutation.
 * @returns TanStack Query mutation result.
 *
 * @example
 * ```tsx
 * const createBinary = useCreateBinary();
 *
 * const handleUpload = async (file: File) => {
 *   const binary = await createBinary.mutateAsync({
 *     data: file,
 *     contentType: file.type,
 *     filename: file.name,
 *   });
 *   console.log('Uploaded:', binary.id, binary.contentType);
 * };
 * ```
 */
export function useCreateBinary(
  options?: MutationOptions<Binary, CreateBinaryInput>,
): UseMutationResult<Binary, OperationOutcomeError, CreateBinaryInput> {
  const medplum = useMedplum();
  const queryClient = useQueryClient();
  const autoInvalidate = options?.autoInvalidate ?? true;

  const { onSuccess: userOnSuccess, ...restMutationOptions } = options?.mutationOptions || {};

  return useMutation({
    mutationFn: async ({ data, contentType, filename }) => {
      try {
        // Convert ArrayBuffer to Uint8Array if needed (medplum.createBinary doesn't accept ArrayBuffer directly)
        const binaryData = data instanceof ArrayBuffer
          ? new Uint8Array(data)
          : data;
        return await medplum.createBinary({ data: binaryData, contentType, filename });
      } catch (error) {
        throw new OperationOutcomeError(normalizeOperationOutcome(error), { cause: error });
      }
    },
    onSuccess: async (data, variables, context) => {
      if (autoInvalidate) {
        await medplumCacheUtilities.invalidateResource(queryClient, 'Binary');
      }
      // Call user's onSuccess if provided
      if (userOnSuccess) {
        await userOnSuccess(data, variables, context);
      }
    },
    ...restMutationOptions,
  });
}

/**
 * React Hook for reading a single FHIR resource by type and ID.
 *
 * This is an imperative hook that returns a function to read resources,
 * useful when you need to read a resource inside an async operation.
 *
 * @returns An object with a `readResource` function.
 *
 * @example
 * ```tsx
 * const { readResource } = useReadResource();
 *
 * const handleUpdate = async () => {
 *   const composition = await readResource('Composition', compositionId);
 *   // Update composition...
 * };
 * ```
 */
export function useReadResource(): {
  readResource: <RT extends ResourceType>(
    resourceType: RT,
    id: string,
  ) => Promise<ExtractResource<RT>>;
} {
  const medplum = useMedplum();

  const readResource = async <RT extends ResourceType>(
    resourceType: RT,
    id: string,
  ): Promise<ExtractResource<RT>> => {
    try {
      return await medplum.readResource(resourceType, id);
    } catch (error) {
      throw new OperationOutcomeError(normalizeOperationOutcome(error), { cause: error });
    }
  };

  return { readResource };
}
